import request from '../request';
import { superAdminAPI } from './urls';
import { config } from '../../utils/config';
import { identityAuth } from '../../auth/IdentityAuth';

function getAdminDashboardInfo() {
  return request({ url: superAdminAPI.getDashboardInfo, method: 'GET' })
    .then(response => response)
    .catch(error => error);
}

function getDashboardSummary() {
  return request({ url: superAdminAPI.getDashboardSummary, method: 'GET' })
    .then(response => response)
    .catch(error => error);
}

function searchSchoolsAndMats(searchTerm, limit = 20) {
  return request({
    url: superAdminAPI.searchSchoolsAndMats,
    method: 'POST',
    data: { searchTerm, limit }
  })
    .then(response => response)
    .catch(error => error);
}

function getSurveysPaginated(surveyType, pageNumber, pageSize, sortBy, sortDirection, searchTerm) {
  return request({
    url: superAdminAPI.getSurveysPaginated,
    method: 'POST',
    data: {
      surveyType: surveyType || 'Welbee',
      pageNumber: pageNumber || 1,
      pageSize: pageSize || 10,
      sortBy: sortBy || 'Id',
      sortDirection: sortDirection || 'desc',
      searchTerm: searchTerm || ''
    }
  })
    .then(response => response)
    .catch(error => error);
}

// Get Insights survey counts summary
async function getInsightsSurveyCounts() {
  // Use production Insights API URL
  const baseUrl = 'https://api.insights.welbee.co.uk/api';
  const url = `${baseUrl}/Survey/GetSummaryForSuperAdmin`;
  
  // Get authentication token
  const token = await identityAuth.getValidToken();
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    method: 'GET',
    headers: headers,
    mode: 'cors'
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, url: ${url}`);
      }
      return response.json();
    })
    .then(data => ({
      success: true,
      data: data || {}
    }))
    .catch(error => {
      console.error('Error fetching Insights survey counts:', error);
      console.error('Attempted URL:', url);
      return {
        success: false,
        data: {}
      };
    });
}

// Search Insights surveys with server-side pagination and filtering
async function searchInsightsSurveys(searchParams) {
  // Use production Insights API URL
  const baseUrl = 'https://api.insights.welbee.co.uk/api';
  const url = `${baseUrl}/Survey/SearchForSuperAdmin`;
  
  // Prepare request body - only include filters that are actually set
  // API requires pageNumber to be > 0 (1-based pagination)
  const requestBody = {
    pageNumber: searchParams.pageNumber !== undefined && searchParams.pageNumber > 0 ? searchParams.pageNumber : 1,
    pageSize: searchParams.pageSize || 10
  };
  
  // Only add filter parameters if they have meaningful values
  if (searchParams.schoolId && searchParams.schoolId > 0) {
    requestBody.schoolId = searchParams.schoolId;
  }
  
  if (searchParams.matId && searchParams.matId > 0) {
    requestBody.matId = searchParams.matId;
  }
  
  if (searchParams.matSurveysOnly === true) {
    requestBody.matSurveysOnly = true;
  }
  
  if (searchParams.schoolSurveysOnly === true) {
    requestBody.schoolSurveysOnly = true;
  }
  
  if (searchParams.searchTerm && searchParams.searchTerm.trim().length > 0) {
    requestBody.searchTerm = searchParams.searchTerm.trim();
  }
  
  if (searchParams.statuses && Array.isArray(searchParams.statuses) && searchParams.statuses.length > 0) {
    requestBody.statuses = searchParams.statuses;
  }
  
  if (searchParams.audience && searchParams.audience > 0) {
    requestBody.audience = searchParams.audience;
  }
  
  // Only include isCheckInSurvey when explicitly set to true
  if (searchParams.isCheckInSurvey === true) {
    requestBody.isCheckInSurvey = true;
  }

  // Always forward explicit boolean for showTestingSurveys when provided
  // Backend handles filtering and pagination correctly
  if (typeof searchParams.showTestingSurveys === 'boolean') {
    requestBody.showTestingSurveys = searchParams.showTestingSurveys;
  }
  
  // Get authentication token
  const token = await identityAuth.getValidToken();
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    method: 'POST',
    headers: headers,
    mode: 'cors',
    body: JSON.stringify(requestBody)
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, url: ${url}`);
      }
      return response.json();
    })
    .then(data => ({
      success: true,
      data: data.surveys || [],
      totalCount: data.totalCount || 0,
      pageNumber: data.pageNumber || 0,
      pageSize: data.pageSize || 10
    }))
    .catch(error => {
      console.error('Error searching Insights surveys:', error);
      console.error('Attempted URL:', url);
      return {
        success: false,
        data: [],
        totalCount: 0,
        pageNumber: 0,
        pageSize: 10
      };
    });
}

const sendRegistrationCompletionEmail = schoolId => {
  return request({
    url: superAdminAPI.sendRegistrationCompletionEmail(schoolId),
    method: 'POST'
  });
};

const superAdminService = {
  getAdminDashboardInfo,
  getDashboardSummary,
  searchSchoolsAndMats,
  getSurveysPaginated,
  searchInsightsSurveys,
  getInsightsSurveyCounts,
  sendRegistrationCompletionEmail
};

export default superAdminService;
